import {Login, TextInput} from 'react-admin';
import {alpha, styled, useTheme} from '@mui/material/styles';
import {AppButton} from '@/components/AppButton';
import {Box, Stack, Typography} from '@mui/material';
import {
    Form,
    required,
    useTranslate,
    useLogin,
    useNotify,
    useSafeSetState,
} from 'ra-core';

const PREFIX = 'RaLoginForm';

const LoginFormClasses = {
    content: `${PREFIX}-content`,
    button: `${PREFIX}-button`,
};

const StyledForm = styled(Form, {
    name: PREFIX,
    overridesResolver: (props, styles) => styles.root,
})(({theme}) => ({
    width: '100%',
    [`& .${LoginFormClasses.content}`]: {
        width: '100%',
        maxWidth: '100%',
        boxSizing: 'border-box',
    },
    [`& .${LoginFormClasses.button}`]: {
        marginTop: theme.spacing(0.5),
    },
}));

interface LoginFormProps {
    redirectTo?: string;
    className?: string;
}

interface FormData {
    email: string;
    password: string;
}

/**
 * BreedBuddies admin login — email field instead of react-admin default username.
 */
const LoginForm = (props: LoginFormProps) => {
    const {redirectTo, className} = props;
    const theme = useTheme();
    const [loading, setLoading] = useSafeSetState(false);
    const login = useLogin();
    const translate = useTranslate();
    const notify = useNotify();

    const submit = (values: FormData) => {
        setLoading(true);
        login(values, redirectTo)
            .then(() => {
                setLoading(false);
            })
            .catch((error) => {
                setLoading(false);
                notify(
                    typeof error === 'string'
                        ? error
                        : typeof error === 'undefined' || !error.message
                          ? 'ra.auth.sign_in_error'
                          : error.message,
                    {
                        type: 'error',
                        messageArgs: {
                            _:
                                typeof error === 'string'
                                    ? error
                                    : error && error.message
                                      ? error.message
                                      : undefined,
                        },
                    }
                );
            });
    };

    const textFieldSx = {
        '& .MuiFormControl-root': {width: '100%'},
        '& .MuiOutlinedInput-root': {
            borderRadius: 2,
            backgroundColor: alpha(theme.palette.common.white, 0.92),
            transition: theme.transitions.create(
                ['border-color', 'box-shadow', 'background-color'],
                {duration: theme.transitions.duration.short}
            ),
            '& fieldset': {
                borderColor: alpha(theme.palette.text.primary, 0.12),
            },
            '&:hover fieldset': {
                borderColor: alpha(theme.palette.primary.main, 0.45),
            },
            '&.Mui-focused': {
                backgroundColor: theme.palette.common.white,
                boxShadow: `0 0 0 3px ${alpha(theme.palette.primary.main, 0.22)}`,
            },
            '&.Mui-focused fieldset': {
                borderWidth: 2,
                borderColor: theme.palette.primary.main,
            },
            '&.Mui-error fieldset': {
                borderColor: theme.palette.error.main,
            },
        },
        '& .MuiInputLabel-root.Mui-focused': {
            color: theme.palette.primary.main,
        },
        '& .MuiFormHelperText-root.Mui-error': {
            color: theme.palette.error.main,
            fontWeight: 500,
            marginTop: theme.spacing(0.75),
        },
    } as const;

    return (
        <StyledForm
            onSubmit={submit}
            mode="onChange"
            noValidate
            className={className}
        >
            <Box
                className={LoginFormClasses.content}
                sx={{
                    px: {xs: 2.5, sm: 3.5},
                    pb: {xs: 2.5, sm: 3.5},
                    pt: {xs: 0, sm: 0.5},
                }}
            >
                <Stack spacing={3}>
                    <Stack spacing={0.75}>
                        <Typography
                            variant="h5"
                            component="h1"
                            sx={{
                                fontWeight: 600,
                                letterSpacing: '-0.02em',
                                color: 'text.primary',
                            }}
                        >
                            BreedBuddies Admin
                        </Typography>
                        <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{lineHeight: 1.5}}
                        >
                            Sign in with your email and password to manage the
                            dashboard.
                        </Typography>
                    </Stack>

                    <Stack spacing={2.25}>
                        <TextInput
                            autoFocus
                            source="email"
                            label="Email"
                            type="email"
                            autoComplete="email"
                            validate={required()}
                            fullWidth
                            sx={textFieldSx}
                        />
                        <TextInput
                            source="password"
                            label={translate('ra.auth.password')}
                            type="password"
                            autoComplete="current-password"
                            validate={required()}
                            fullWidth
                            sx={textFieldSx}
                        />
                    </Stack>

                    <AppButton
                        variant="primary"
                        type="submit"
                        loading={loading}
                        fullWidth
                        size="large"
                        className={LoginFormClasses.button}
                        sx={{
                            py: 1.25,
                            '&:focus-visible': {
                                outline: `3px solid ${alpha(theme.palette.primary.main, 0.45)}`,
                                outlineOffset: 2,
                            },
                            '&.Mui-disabled': {
                                opacity: 0.75,
                            },
                        }}
                    >
                        {translate('ra.auth.sign_in')}
                    </AppButton>
                </Stack>
            </Box>
        </StyledForm>
    );
};

const StyledLogin = styled(Login)(({theme}) => ({
    minHeight: '100vh',
    boxSizing: 'border-box',
    padding: theme.spacing(2),
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: theme.palette.grey[50],
    backgroundImage: `linear-gradient(
        165deg,
        ${theme.palette.common.white} 0%,
        ${alpha(theme.palette.primary.main, 0.045)} 48%,
        ${alpha(theme.palette.grey[200], 0.45)} 100%
    )`,
    '& .MuiCard-root': {
        maxWidth: 440,
        width: '100%',
        overflow: 'hidden',
        borderRadius: theme.spacing(2),
        border: `1px solid ${alpha(theme.palette.common.black, 0.08)}`,
        boxShadow: `0 12px 40px ${alpha(theme.palette.common.black, 0.1)}`,
    },
    '& .MuiAvatar-root': {
        width: 64,
        height: 64,
        marginLeft: 'auto',
        marginRight: 'auto',
        boxShadow: `0 4px 16px ${alpha(theme.palette.common.black, 0.12)}`,
        border: `2px solid ${alpha(theme.palette.common.white, 0.95)}`,
    },
}));

const Logo = styled('img')({
    width: '100%',
    height: '100%',
    objectFit: 'contain',
    display: 'block',
});

export const LoginPage = () => (
    <StyledLogin avatarIcon={<Logo src="/favicon.png" alt="BreedBuddies" />}>
        <LoginForm />
    </StyledLogin>
);
